<template>
  <div class="w-100">
    <div v-if="loading" class="loading-spinner">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Fájlok betöltése...</span>
    </div>
    <div v-else-if="error" class="error-state">
      <i class="fas fa-exclamation-triangle"></i>
      <p>Hiba történt a fájlok betöltése során</p>
      <button class="btn btn-primary btn-sm" @click="loadFiles">
        <i class="fas fa-redo"></i>
        Újrapróbálás
      </button>
    </div>
    <div v-else-if="files.length === 0" class="empty-state">
      <i class="fas fa-inbox"></i>
      <p>Nincsenek fájlok</p>
    </div>
    <div v-else>
      <!-- Keresőmező -->
      <div class="search-container">
        <div class="search-box">
          <i class="fas fa-search"></i>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Keresés fájlnév vagy leírás alapján..."
            class="search-input"
          />
          <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="search-stats" v-if="searchQuery">
          {{ filteredFiles.length }} találat a(z) "{{ searchQuery }}" keresésre
        </div>
      </div>

      <!-- Folytonos táblázat -->
      <div class="files-container">
        <div class="files-table">
          <!-- Kategóriákkal szeparált sorok -->
          <template v-for="(categoryFiles, category) in categorizedFiles" :key="category">
            <!-- Kategória szeparátor -->
            <div class="category-separator">
              <div class="category-info">
                <i :class="getCategoryIcon(category)" class="category-icon"></i>
                <span class="category-name">{{ getCategoryName(category) }}</span>
                <span class="category-count">({{ categoryFiles.length }})</span>
              </div>
              <button 
                class="toggle-category" 
                @click="toggleCategory(category)"
                :class="{ collapsed: collapsedCategories.includes(category) }"
              >
                <i class="fas fa-chevron-down"></i>
              </button>
            </div>
            
            <!-- Kategória fájljai -->
            <template v-if="!collapsedCategories.includes(category)">
              <div 
                v-for="file in categoryFiles" 
                :key="file.id" 
                class="file-row"
                @click="downloadFile(file.id)"
              >
                <div class="col-icon">
                  <div class="file-icon-small">
                    <i :class="getFileIcon(file.type)"></i>
                  </div>
                </div>
                <div class="col-name">
                  <span class="file-name">{{ file.name }}</span>
                  <span class="file-type-badge">{{ file.type }}</span>
                </div>
                <div class="col-description">
                  <span class="file-description-text">{{ file.description || '-' }}</span>
                </div>
                <div class="col-size">
                  <span class="file-size-text">{{ file.file_size || '-' }}</span>
                </div>
                <div class="col-date">
                  <span class="file-date-text" v-if="file.created_at">
                    {{ formatDate(file.created_at) }}
                  </span>
                  <span v-else>-</span>
                </div>
                <div class="col-actions">
                  <button 
                    class="action-btn download-btn" 
                    @click.stop="downloadFile(file.id)"
                    title="Letöltés"
                  >
                    <i class="fas fa-download"></i>
                  </button>
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';

export default {
  name: 'FileCard',
  props: {
    type: {
      type: [String, Array],
      default: null
    }
  },
  data() {
    return {
      files: [],
      fileCount: 0,
      notShownCount: 0,
      loading: false,
      error: false,
      searchQuery: '',
      collapsedCategories: []
    }
  },
  computed: {
    filteredFiles() {
      if (!this.searchQuery.trim()) {
        return this.files;
      }
      const query = this.searchQuery.toLowerCase();
      return this.files.filter(file => 
        file.name.toLowerCase().includes(query) ||
        (file.description && file.description.toLowerCase().includes(query))
      );
    },
    
    categorizedFiles() {
      const categories = {};
      this.filteredFiles.forEach(file => {
        const category = file.type || 'Egyéb';
        if (!categories[category]) {
          categories[category] = [];
        }
        categories[category].push(file);
      });
      
      // Rendezés kategória név szerint
      const sortedCategories = {};
      Object.keys(categories)
        .sort()
        .forEach(key => {
          // Fájlok rendezése név szerint kategórián belül
          sortedCategories[key] = categories[key].sort((a, b) => 
            a.name.localeCompare(b.name, 'hu')
          );
        });
      
      return sortedCategories;
    }
  },
  async mounted() {
    await this.loadFiles();
  },
  methods: {
    async loadFiles() {
      try {
        this.loading = true;
        this.error = false;

        const params = {};
        if (this.type) {
          if (Array.isArray(this.type)) {
            params.type = this.type.join(',');
          } else {
            params.type = this.type;
          }
        }

        const response = await apiService.get('/partner-files', { params });

        const payload = response && response.data ? response.data : response;
        if (payload && payload.files) {
          this.files = payload.files || [];
          this.fileCount = payload.total || this.files.length;
          this.notShownCount = Math.max(0, this.fileCount - this.files.length);
        } else {
          throw new Error('Failed to load files');
        }
      } catch (err) {
        console.error('Error loading files:', err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    async downloadFile(fileId) {
      try {
        this.loading = true;
        const response = await apiService.get(`/partner-files/${fileId}/download`, { responseType: 'blob' });
        const blob = response && response.data ? response.data : response;
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        const disposition = response && response.headers ? response.headers['content-disposition'] : null;
        let filename = `file_${fileId}`;
        if (disposition) {
          const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)/i.exec(disposition);
          if (match && match[1]) filename = decodeURIComponent(match[1]);
        }
        link.setAttribute('download', filename + (filename.endsWith('.pdf') || filename.includes('.') ? '' : '.pdf'));
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error downloading file:', err);
        if (this.$emit) {
          this.$emit('show-alert', { type: 'error', message: 'Hiba történt a fájl letöltése során.' });
        } else if (this.$swal && this.$swal.fire) {
          this.$swal.fire({
            title: 'Hiba',
            text: 'Nem sikerült letölteni a fájlt',
            icon: 'error'
          });
        }
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('hu-HU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    getCategoryName(category) {
      const categoryNames = {
        'pdf': 'PDF dokumentumok',
        'doc': 'Word dokumentumok',
        'docx': 'Word dokumentumok', 
        'xls': 'Excel táblázatok',
        'xlsx': 'Excel táblázatok',
        'ppt': 'PowerPoint prezentációk',
        'pptx': 'PowerPoint prezentációk',
        'txt': 'Szöveges fájlok',
        'image': 'Képek',
        'jpg': 'Képek',
        'jpeg': 'Képek',
        'png': 'Képek',
        'gif': 'Képek'
      };
      return categoryNames[category.toLowerCase()] || category;
    },
    
    getCategoryIcon(category) {
      const categoryIcons = {
        'pdf': 'fas fa-file-pdf',
        'doc': 'fas fa-file-word',
        'docx': 'fas fa-file-word',
        'xls': 'fas fa-file-excel',
        'xlsx': 'fas fa-file-excel',
        'ppt': 'fas fa-file-powerpoint',
        'pptx': 'fas fa-file-powerpoint',
        'txt': 'fas fa-file-alt',
        'image': 'fas fa-file-image',
        'jpg': 'fas fa-file-image',
        'jpeg': 'fas fa-file-image',
        'png': 'fas fa-file-image',
        'gif': 'fas fa-file-image'
      };
      return categoryIcons[category.toLowerCase()] || 'fas fa-file';
    },
    
    getFileIcon(type) {
      return this.getCategoryIcon(type);
    },
    
    toggleCategory(category) {
      const index = this.collapsedCategories.indexOf(category);
      if (index > -1) {
        this.collapsedCategories.splice(index, 1);
      } else {
        this.collapsedCategories.push(category);
      }
    }
  }
}
</script>

<style scoped>
/* Keresőmező stílusok */
.search-container {
  margin-bottom: 1.5rem;
}

.search-box {
  position: relative;
  max-width: 400px;
  margin: 0 auto 1rem;
}

.search-box i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  z-index: 2;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s ease;
  background: white;
}

.search-input:focus {
  outline: none;
  border-color: #1d4699;
  box-shadow: 0 0 0 2px rgba(29, 70, 153, 0.1);
}

.clear-search {
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.clear-search:hover {
  background: #f8f9fa;
  color: #1d4699;
}

.search-stats {
  text-align: center;
  color: #6c757d;
  font-size: 0.8rem;
  font-style: italic;
}

/* Fájlok konténer */
.files-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid #e9ecef;
}

/* Táblázat stílusok */
.files-table {
  width: 100%;
}

/* Kategória szeparátor */
.category-separator {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f1f3f5;
  border-bottom: 1px solid #e9ecef;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.category-separator:hover {
  background: #e9ecef;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.category-icon {
  color: #1d4699;
  width: 16px;
  text-align: center;
}

.category-count {
  font-weight: 400;
  color: #6c757d;
  font-size: 0.8rem;
}

.toggle-category {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-category:hover {
  background: #dee2e6;
  color: #1d4699;
}

.toggle-category.collapsed i {
  transform: rotate(-90deg);
}

.toggle-category i {
  transition: transform 0.2s ease;
  font-size: 0.8rem;
}

/* Fájl sorok */
.file-row {
  display: grid;
  grid-template-columns: 40px 2fr 2fr 1fr 1fr 80px;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
  transition: background-color 0.2s ease;
  align-items: center;
}

.file-row:hover {
  background: #f8f9fa;
}

.file-row:last-child {
  border-bottom: none;
}

.file-icon-small {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
  color: #495057;
  font-size: 0.8rem;
}

.col-name {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.file-name {
  font-weight: 500;
  color: #2c3e50;
  word-break: break-word;
  font-size: 0.9rem;
  line-height: 1.3;
}

.file-type-badge {
  font-size: 0.7rem;
  background: #e9ecef;
  color: #495057;
  padding: 0.1rem 0.4rem;
  border-radius: 8px;
  width: fit-content;
  text-transform: uppercase;
  font-weight: 500;
}

.file-description-text {
  color: #6c757d;
  word-break: break-word;
  line-height: 1.3;
  font-size: 0.85rem;
}

.file-size-text,
.file-date-text {
  color: #495057;
  font-size: 0.8rem;
}

.col-actions {
  display: flex;
  justify-content: center;
}

.action-btn {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  color: #1d4699;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
}

.action-btn:hover {
  background: #1d4699;
  color: white;
  border-color: #1d4699;
}

/* Eredeti loading/error stílusok */
.loading-spinner,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  width: 100%;
  max-width: 250px;
  margin: auto;
  min-height: 200px; /* Minimum magasság a vertikális középre igazításhoz */
}

.loading-spinner i,
.error-state i,
.empty-state i {
  font-size: 24px;
  margin-bottom: 10px;
  /* display: block; eltávolítva, mert a flexbox kezeli */
}

.loading-spinner {
  color: #007bff;
}

.loading-spinner i {
  animation: spin 1s linear infinite;
}

.error-state {
  color: #dc3545;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Reszponzív dizájn */
@media (max-width: 1200px) {
  .file-row {
    grid-template-columns: 40px 2fr 1.5fr 80px 80px 60px;
  }
  
  .col-description {
    display: none;
  }
}

@media (max-width: 768px) {
  .search-box {
    max-width: none;
    margin: 0 0 1rem;
  }
  
  .file-row {
    grid-template-columns: 40px 1fr 80px 60px;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
  }
  
  .col-description,
  .col-size {
    display: none;
  }
  
  .category-separator {
    padding: 0.5rem 0.75rem;
  }
}

@media (max-width: 576px) {
  .file-row {
    grid-template-columns: 1fr 50px;
    gap: 0.5rem;
  }
  
  .col-icon,
  .col-size,
  .col-date {
    display: none;
  }
  
  .col-name {
    grid-column: 1;
  }
  
  .file-name {
    font-size: 0.85rem;
  }
  
  .search-input {
    padding: 0.5rem 0.75rem 0.5rem 2rem;
  }
  
  .category-separator {
    grid-template-columns: 1fr auto;
  }
}
</style>